import { useCallback, useEffect, useState } from 'react';
import { api, ApiError } from '../api/client';
import { useAuth } from '../state/auth';
import { Bandeau, Carte, Champ, Modale, Squelette, Vide } from '../components/ui';
import { centimes, dateFr, deviseReference, isoJour, moisLong, montant } from '../lib/format';
import { T, langue, reglagesRegionaux } from '../i18n';

const nombre = (n: number) => n.toLocaleString(langue === 'en' ? 'en-GB' : 'fr-FR');
import type { BilanEntretiens, Vehicule } from '../api/types';

interface Entretien {
  _id: string; type: string; date: string; km: number; coutMinor: number;
  prestataire?: string; description?: string;
  immobilisationDebut?: string; immobilisationFin?: string;
  prochaineEcheanceKm?: number; prochaineEcheanceDate?: string;
  vehicle?: { _id: string; marque: string; modele: string; immatriculation: string };
}

export default function Entretien() {
  const { peut } = useAuth();
  const [liste, setListe] = useState<Entretien[] | null>(null);
  const [vehicules, setVehicules] = useState<Vehicule[]>([]);
  const [ouvert, setOuvert] = useState(false);
  const [bilan, setBilan] = useState<BilanEntretiens | null>(null);
  const [vueBilan, setVueBilan] = useState(false);
  const [f, setF] = useState({
    vehicle: '', type: 'Vidange', date: isoJour(new Date()), km: '', cout: '',
    prestataire: '', description: '', immoDebut: '', immoFin: '', prochainKm: '',
    // Le garagiste est payé avec l'argent du tiroir : par défaut, ça sort de la caisse.
    sortieCaisse: true,
  });
  const [erreur, setErreur] = useState('');
  /* « Quelle voiture, quel mois » : sur treize voitures et des annees
   * d'interventions, une liste a plat ne se lit plus. */
  const [filtreVehicule, setFiltreVehicule] = useState('');
  const [filtreMois, setFiltreMois] = useState('');

  const charger = useCallback(() => { api.get<Entretien[]>('/operations/entretiens').then(setListe); }, []);
  useEffect(() => { charger(); api.get<Vehicule[]>('/vehicules').then(setVehicules); }, [charger]);
  if (!liste) return <Squelette lignes={6} />;

  async function creer() {
    setErreur('');
    try {
      await api.post('/operations/entretiens', {
        vehicle: f.vehicle, type: f.type, date: f.date,
        km: Number(f.km || 0), coutMinor: centimes(Number(f.cout || 0)),
        prestataire: f.prestataire || undefined, description: f.description || undefined,
        immobilisationDebut: f.immoDebut || undefined, immobilisationFin: f.immoFin || undefined,
        prochaineEcheanceKm: f.prochainKm ? Number(f.prochainKm) : undefined,
        sortieCaisse: f.sortieCaisse,
      });
      setOuvert(false); charger();
    } catch (e) { setErreur(e instanceof ApiError ? e.erreur : T.entretien.erreur); }
  }

  const visibles = liste.filter((e) => (
    (!filtreVehicule || e.vehicle?._id === filtreVehicule)
    && (!filtreMois || e.date.slice(0, 7) === filtreMois)));
  const total = visibles.reduce((a, e) => a + e.coutMinor, 0);
  /* Les mois qui existent vraiment, du plus recent au plus ancien : proposer
   * douze mois vides ferait chercher dans des listes sans reponse. */
  const moisDisponibles = [...new Set(liste.map((e) => e.date.slice(0, 7)))].sort().reverse();
  const nomMois = (m: string) => {
    const [an, n] = m.split('-').map(Number);
    return moisLong(new Date(an!, n! - 1, 1));
  };
  const aVenir = vehicules
    .map((v) => ({ v, dernier: liste.find((e) => e.vehicle?._id === v._id) }))
    .filter((x) => x.dernier?.prochaineEcheanceKm && v_reste(x.v.km, x.dernier.prochaineEcheanceKm) < 2000);

  function v_reste(km: number, echeance: number) { return echeance - km; }

  const te = T.entretien;
  const u = reglagesRegionaux().uniteDistance;
  return (
    <>
      <div className="entete">
        <div className="titres">
          <h1>{te.titre}</h1>
          <p>
            {te.resume(visibles.length, montant(total))}
            {(filtreVehicule || filtreMois) && te.surTotal(liste.length)}
          </p>
        </div>
        <div className="actions">
          <button className={`btn ${vueBilan ? 'btn-p' : ''}`}
                  onClick={() => { setVueBilan(!vueBilan); if (!bilan) void api.get<BilanEntretiens>('/operations/entretiens/bilan').then(setBilan).catch(() => {}); }}>
            {vueBilan ? te.voirInterventions : te.voirBilan}
          </button>
          {peut('modifier.vehicule') && (
            <button className="btn btn-p" onClick={() => setOuvert(true)}>{te.enregistrer}</button>
          )}
        </div>
      </div>

      {/*
        * Le bilan, pas la liste.
        *
        * La question n'est pas « qu'a-t-on fait sur la Clio » mais « quelle
        * voiture me coute le plus cher, et combien de jours m'a-t-elle fait
        * perdre ». Une voiture au garage ne loue pas : le manque a gagner est
        * la moitie invisible de la facture, et il depasse souvent la facture.
        */}
      {vueBilan && (
        bilan === null ? <Squelette lignes={5} /> : (
          <>
            <div className="grille g4" style={{ marginBottom: 16 }}>
              <div className="carte carte-p">
                <div className="micro">{te.depense}</div>
                <div className="v montant">{montant(bilan.totalCoutMinor)}</div>
              </div>
              <div className="carte carte-p">
                <div className="micro">{te.joursImmo}</div>
                <div className="v">{bilan.totalJoursImmobilises}</div>
              </div>
              <div className="carte carte-p">
                <div className="micro">{te.manque}</div>
                <div className="v montant" style={{ color: 'var(--danger)' }}>{montant(bilan.totalManqueAGagnerMinor)}</div>
                <div className="mini sourdine">{te.manqueAide}</div>
              </div>
              <div className="carte carte-p">
                <div className="micro">{te.coutReel}</div>
                <div className="v montant">{montant(bilan.totalCoutMinor + bilan.totalManqueAGagnerMinor)}</div>
                <div className="mini sourdine">{te.coutReelAide}</div>
              </div>
            </div>

            <Carte pad={false} titre={te.parVoiture}>
              <div className="enveloppe-tbl">
                <table className="tbl">
                  <thead>
                    <tr>
                      <th>{te.colBilan.vehicule}</th><th className="droite">{te.colBilan.interventions}</th>
                      <th className="droite">{te.colBilan.depense}</th><th className="droite">{te.colBilan.jours}</th>
                      <th className="droite">{te.colBilan.manque}</th><th className="droite">{te.coutPar(u)}</th>
                      <th>{te.colBilan.prochaine}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bilan.lignes.filter((l) => l.nbEntretiens > 0).map((l) => (
                      <tr key={l.vehicleId}>
                        <td><b>{l.libelle}</b><div className="mini sourdine">{l.immatriculation} · {nombre(l.km)} {u}</div></td>
                        <td className="droite num">{l.nbEntretiens}</td>
                        <td className="droite montant">{montant(l.coutTotalMinor)}</td>
                        <td className="droite num">{l.joursImmobilises}</td>
                        <td className="droite montant" style={{ color: 'var(--danger)' }}>{montant(l.manqueAGagnerMinor)}</td>
                        <td className="droite montant">{montant(l.coutParKmMinor)}</td>
                        <td className="mini" style={{ color: l.prochaine?.includes('dépassée') ? 'var(--danger)' : 'var(--texte2)' }}>
                          {l.prochaine ?? '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Carte>
            <p className="mini sourdine" style={{ marginTop: 12 }}>
              {te.noteBilan(u)}
            </p>
          </>
        )
      )}

      {!vueBilan && aVenir.length > 0 && (
        <div style={{ marginBottom: 14, display: 'grid', gap: 8 }}>
          {aVenir.map((x) => (
            <Bandeau key={x.v._id} type="warn" titre={`${x.v.marque} ${x.v.modele} — ${x.v.immatriculation}`}>
              {te.alerte(nombre(x.dernier!.prochaineEcheanceKm!), nombre(x.v.km), nombre(v_reste(x.v.km, x.dernier!.prochaineEcheanceKm!)), u)}
            </Bandeau>
          ))}
        </div>
      )}

      {!vueBilan && (
      <Carte pad={false} action={
        <div className="actions">
          <select value={filtreVehicule} onChange={(e) => setFiltreVehicule(e.target.value)}
                  style={{ height: 32, borderRadius: 8, border: '1px solid var(--bord-fort)', padding: '0 8px' }}>
            <option value="">{te.toutesVoitures}</option>
            {vehicules.map((v) => (
              <option key={v._id} value={v._id}>{v.marque} {v.modele} — {v.immatriculation}</option>
            ))}
          </select>
          <select value={filtreMois} onChange={(e) => setFiltreMois(e.target.value)}
                  style={{ height: 32, borderRadius: 8, border: '1px solid var(--bord-fort)', padding: '0 8px' }}>
            <option value="">{te.tousMois}</option>
            {moisDisponibles.map((m) => <option key={m} value={m}>{nomMois(m)}</option>)}
          </select>
          {(filtreVehicule || filtreMois) && (
            <button className="btn btn-s" onClick={() => { setFiltreVehicule(''); setFiltreMois(''); }}>
              {te.toutVoir}
            </button>
          )}
        </div>
      } titre={te.interventions}>
        {visibles.length === 0 ? (
          <div className="carte-p">
            <Vide titre={liste.length === 0 ? te.aucun : te.aucunFiltre} />
          </div>
        ) : (
          <div className="enveloppe-tbl">
            <table className="tbl">
              <thead>
                <tr><th>{te.col.date}</th><th>{te.col.vehicule}</th><th>{te.col.intervention}</th><th>{te.col.prestataire}</th>
                    <th className="droite">{u === 'km' ? te.col.km : u}</th><th className="droite">{te.col.cout}</th><th>{te.col.immo}</th><th className="droite">{te.col.prochaine}</th></tr>
              </thead>
              <tbody>
                {visibles.map((e) => (
                  <tr key={e._id}>
                    <td className="num">{dateFr(e.date)}</td>
                    <td><b>{e.vehicle?.marque} {e.vehicle?.modele}</b><div className="mini sourdine">{e.vehicle?.immatriculation}</div></td>
                    <td>{te.types[e.type] ?? e.type}{e.description && <div className="mini sourdine">{e.description}</div>}</td>
                    <td className="mini sourdine">{e.prestataire ?? '—'}</td>
                    <td className="droite num">{nombre(e.km)}</td>
                    <td className="droite montant">{montant(e.coutMinor)}</td>
                    <td className="mini sourdine">{e.immobilisationDebut ? `${dateFr(e.immobilisationDebut)} → ${dateFr(e.immobilisationFin)}` : '—'}</td>
                    <td className="droite num mini">{e.prochaineEcheanceKm ? `${nombre(e.prochaineEcheanceKm)} ${u}` : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Carte>
      )}

      {!vueBilan && (
      <p className="mini sourdine" style={{ marginTop: 12 }}>
        {te.noteImmo}
      </p>
      )}

      {ouvert && (
        <Modale titre={te.enregistrer} onFermer={() => setOuvert(false)}>
          <div style={{ display: 'grid', gap: 12 }}>
            <Champ label={te.vehicule}>
              <select value={f.vehicle} onChange={(e) => setF({ ...f, vehicle: e.target.value, km: String(vehicules.find((v) => v._id === e.target.value)?.km ?? '') })}>
                <option value="">{te.choisir}</option>
                {vehicules.map((v) => <option key={v._id} value={v._id}>{v.marque} {v.modele} — {v.immatriculation}</option>)}
              </select>
            </Champ>
            <div className="grille g2">
              <Champ label={te.intervention}>
                <select value={f.type} onChange={(e) => setF({ ...f, type: e.target.value })}>
                  {['Vidange', 'Pneus', 'Plaquettes', 'Révision', 'Panne', 'Carrosserie', 'Autre'].map((t) => <option key={t} value={t}>{te.types[t] ?? t}</option>)}
                </select>
              </Champ>
              <Champ label={te.date}><input type="date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} /></Champ>
            </div>
            <div className="grille g2">
              <Champ label={te.kilometrage}><input type="number" value={f.km} onChange={(e) => setF({ ...f, km: e.target.value })} /></Champ>
              <Champ label={te.cout(deviseReference())}><input type="number" value={f.cout} onChange={(e) => setF({ ...f, cout: e.target.value })} /></Champ>
            </div>
            <Champ label={te.prestataire}><input value={f.prestataire} onChange={(e) => setF({ ...f, prestataire: e.target.value })} /></Champ>
            {/* L'entretien sort de la caisse des dépenses : sans ça, le solde du tiroir
              * ne correspond plus à ce qu'il contient réellement. */}
            <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', margin: '4px 0 2px' }}>
              <input type="checkbox" checked={f.sortieCaisse}
                     onChange={(e) => setF({ ...f, sortieCaisse: e.target.checked })} />
              <span>
                {te.payeCaisse}
                <div className="mini sourdine">
                  {te.payeCaisseAide}
                </div>
              </span>
            </label>
            <div className="grille g2">
              <Champ label={te.immoDu}><input type="date" value={f.immoDebut} onChange={(e) => setF({ ...f, immoDebut: e.target.value })} /></Champ>
              <Champ label={te.immoAu}><input type="date" value={f.immoFin} onChange={(e) => setF({ ...f, immoFin: e.target.value })} /></Champ>
            </div>
            <Champ label={te.prochaineKm(u)} aide={te.prochaineKmAide(u)}>
              <input type="number" value={f.prochainKm} onChange={(e) => setF({ ...f, prochainKm: e.target.value })} />
            </Champ>
            <Champ label={te.description}><textarea value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} /></Champ>
          </div>
          {erreur && <div style={{ marginTop: 10 }}><Bandeau type="ko">{erreur}</Bandeau></div>}
          <div className="actions" style={{ marginTop: 14, justifyContent: 'flex-end' }}>
            <button className="btn" onClick={() => setOuvert(false)}>{te.annuler}</button>
            <button className="btn btn-p" disabled={!f.vehicle} onClick={() => void creer()}>{te.valider}</button>
          </div>
        </Modale>
      )}
    </>
  );
}
